import { useEffect } from 'react';
import { useThemeStore } from '../stores/useThemeStore';

interface PageMeta {
  title: string;
  description?: string;
  /** When true, no clinic-name suffix is appended to the title. */
  exact?: boolean;
}

const upsertMeta = (name: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertProp = (property: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`,
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

/**
 * Sets the document title, meta description, OpenGraph + Twitter card,
 * canonical URL, and `robots: noindex` (this is an authenticated EMR —
 * we never want internal pages indexed). Restores the previous title on
 * unmount so back-navigation reflects the parent page.
 */
export const usePageMeta = ({ title, description, exact }: PageMeta) => {
  const branding = useThemeStore((s) => s.branding);

  useEffect(() => {
    const previousTitle = document.title;
    const fullTitle = exact
      ? title
      : `${title} · ${branding.hospitalName || 'MAIE'}`;
    document.title = fullTitle;

    if (description) {
      upsertMeta('description', description);
      upsertProp('og:description', description);
      upsertMeta('twitter:description', description);
    }
    upsertProp('og:title', fullTitle);
    upsertProp('og:type', 'website');
    upsertProp('og:site_name', branding.hospitalName || 'MAIE');
    upsertMeta('twitter:title', fullTitle);
    upsertMeta('twitter:card', 'summary');

    // Authenticated EMR: explicitly disallow indexing of every inner page
    // so search engines never crawl URLs that depend on session state.
    upsertMeta('robots', 'noindex, nofollow');

    if (typeof window !== 'undefined') {
      upsertCanonical(window.location.origin + window.location.pathname);
    }

    return () => {
      document.title = previousTitle;
    };
  }, [title, description, exact, branding.hospitalName]);
};
